import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CORRENTE_COLORS, horaPermitida, originColor, painelApi, type Granularidade } from '@/api/painel';
import { Donut } from '@/components/painel/charts/Donut';
import { Heatmap } from '@/components/painel/charts/Heatmap';
import { StackedBars } from '@/components/painel/charts/StackedBars';
import { ErrorBox, EstimatedTag, Kpi, SectionTitle } from '@/components/painel/Kpi';
import { PeriodPicker } from '@/components/painel/PeriodPicker';
import { useLoad, usePainelPeriod } from '@/components/painel/PainelContext';
import { StatusSummary } from '@/components/painel/StatusSummary';
import {
  fmtBrl,
  fmtCo2,
  fmtDate,
  fmtInt,
  fmtKwh,
  fmtKwhNum,
  fmtPct,
  labelFormatter
} from '@/components/painel/format';

type Medida = 'kwh' | 'recargas' | 'receita';

const MEDIDAS: { key: Medida; label: string; titulo: string }[] = [
  { key: 'kwh', label: 'kWh', titulo: 'Energia' },
  { key: 'recargas', label: 'Recargas', titulo: 'Recargas' },
  { key: 'receita', label: 'R$', titulo: 'Receita' }
];

const GRANULARIDADES: { key: Granularidade; label: string; por: string }[] = [
  { key: 'hora', label: 'Hora', por: 'por hora' },
  { key: 'dia', label: 'Dia', por: 'por dia' },
  { key: 'mes', label: 'Mês', por: 'por mês' }
];

function Toggle<T extends string>({
  items,
  value,
  onChange,
  disabled
}: {
  items: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  disabled?: (k: T) => boolean;
}) {
  return (
    <div className="flex gap-1 rounded-full bg-white p-0.5 text-[12px] font-semibold">
      {items.map((m) => {
        const off = disabled?.(m.key) ?? false;
        return (
          <button
            key={m.key}
            type="button"
            aria-pressed={value === m.key}
            disabled={off}
            title={off ? 'Por hora só em períodos de até 7 dias' : undefined}
            onClick={() => onChange(m.key)}
            className={`rounded-full px-3 py-1 disabled:opacity-35 ${
              value === m.key ? 'bg-bow-royal text-bow-cream' : 'text-bow-royal/60'
            }`}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

/** Visão geral da org (ref. Analytics › Visão geral da Tupi): KPIs com variação,
 *  status agora, AC/DC, evolução com o período anterior, heatmap e carregadores. */
export default function PainelOverview() {
  const { orgId = '' } = useParams();
  const [period, setPeriod] = usePainelPeriod();
  const key = JSON.stringify(period);
  const [medida, setMedida] = useState<Medida>('kwh');
  const [gran, setGran] = useState<Granularidade>('dia');

  const ov = useLoad(() => painelApi.overview(orgId, period), [orgId, key]);
  const o = ov.data;
  const podeHora = horaPermitida(period);
  // "Hora" num período longo: pede "dia" até o efeito abaixo ajustar o estado.
  const granEfetiva: Granularidade = gran === 'hora' && !podeHora ? 'dia' : gran;
  const series = useLoad(() => painelApi.series(orgId, period, granEfetiva), [orgId, key, granEfetiva]);
  const heat = useLoad(() => painelApi.heatmap(orgId, period), [orgId, key]);
  const chargers = useLoad(() => painelApi.chargers(orgId, period), [orgId, key]);
  const live = useLoad(() => painelApi.stationsLive(orgId), [orgId]);

  // Período trocado pra mais de 7 dias com "Hora" marcado: volta pra "Dia".
  useEffect(() => {
    if (gran === 'hora' && !podeHora) setGran('dia');
  }, [gran, podeHora]);

  const fading = ov.loading && !!o ? 'opacity-60 transition-opacity' : 'transition-opacity';
  const a = o?.anterior ?? null;
  const parcial = o?.anterior_parcial ?? false;
  const co2 = o ? fmtCo2(o.co2_evitado_kg) : null;
  const med = MEDIDAS.find((m) => m.key === medida)!;
  const por = GRANULARIDADES.find((g) => g.key === granEfetiva)!.por;
  const s = series.data;

  return (
    <div className="pt-1">
      <PeriodPicker value={period} onChange={setPeriod} label={o?.period.label} />
      {ov.error && !o && <ErrorBox message={ov.error} onRetry={ov.reload} />}
      {o && co2 && (
        <div className={fading}>
          {o.org.acesso_desde && (
            <p className="m-0 mt-3 text-[12.5px] text-bow-royal/55">
              Seus dados começam em {fmtDate(o.org.acesso_desde)} — recargas anteriores ao seu vínculo
              não entram nos números.
            </p>
          )}
          <div className="mt-4 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            <div className="col-span-2">
              <Kpi
                hero
                label="Receita do período"
                value={fmtBrl(o.receita_total_brl)}
                delta={{ atual: Number(o.receita_total_brl), anterior: a && Number(a.receita_total_brl), parcial }}
                note={
                  <>
                    App Bow {fmtBrl(o.receita_bow_brl)} · roaming {fmtBrl(o.receita_roaming_brl)}
                    <EstimatedTag />
                  </>
                }
              />
            </div>
            <Kpi
              label="Energia entregue"
              value={fmtKwhNum(o.kwh)}
              unit="kWh"
              delta={{ atual: o.kwh, anterior: a?.kwh, parcial }}
            />
            <Kpi
              label="Recargas"
              value={fmtInt(o.recargas)}
              delta={{ atual: o.recargas, anterior: a?.recargas, parcial }}
              note={`Ticket médio ${fmtBrl(o.ticket_medio_brl)}`}
            />
            <Kpi
              label="Clientes"
              value={fmtInt(o.clientes_unicos)}
              delta={{ atual: o.clientes_unicos, anterior: a?.clientes_unicos, parcial }}
              note="Motoristas distintos do app Bow"
            />
            <Kpi
              label="Média por recarga"
              value={fmtKwhNum(o.kwh_medio)}
              unit="kWh"
              delta={{ atual: o.kwh_medio, anterior: a?.kwh_medio, parcial }}
            />
            <Kpi
              label="CO₂ evitado"
              value={co2.value}
              unit={co2.unit}
              delta={{ atual: o.co2_evitado_kg, anterior: a?.co2_evitado_kg, parcial }}
            />
            <Kpi
              label="Carregadores"
              value={fmtInt(o.org.carregadores)}
              note={`${fmtInt(o.estacoes)} ${o.estacoes === 1 ? 'estação' : 'estações'}`}
            />
            <Kpi
              label="Ocupação"
              value={fmtPct(o.ocupacao_pct)}
              delta={{ atual: o.ocupacao_pct, anterior: a?.ocupacao_pct, parcial }}
              note="Tempo com carro carregando ÷ tempo disponível"
            />
            <Kpi
              label="Disponibilidade"
              value={o.uptime_pct == null ? 'sem dados' : fmtPct(o.uptime_pct)}
              note={
                o.uptime_pct == null
                  ? 'Medição começou agora; o histórico vai aparecer aqui.'
                  : 'Tempo no ar (falha do Hub não conta)'
              }
            />
            <Kpi
              label="NPS"
              value={o.nps.score == null ? '—' : fmtInt(o.nps.score)}
              note={`${fmtInt(o.nps.respostas)} avaliações no app Bow`}
            />
          </div>

          <div className="grid gap-x-2.5 lg:grid-cols-2">
            <div>
              <SectionTitle>Agora</SectionTitle>
              <div className="rounded-3xl bg-white p-4 shadow-soft">
                {live.data && (
                  <StatusSummary resumo={live.data.resumo} hubDisponivel={live.data.hub_disponivel} />
                )}
                {live.error && !live.data && <ErrorBox message={live.error} onRetry={live.reload} />}
                {!live.data && !live.error && <div className="h-[104px] animate-pulse rounded-2xl bg-bow-cream/60" />}
              </div>
            </div>
            <div>
              <SectionTitle>AC e DC no período</SectionTitle>
              <div className="rounded-3xl bg-white p-4 shadow-soft">
                <Donut
                  ariaLabel="Energia por tipo de corrente"
                  centerLabel="kWh no período"
                  formatValue={fmtKwhNum}
                  items={o.por_corrente.map((c) => ({
                    key: c.corrente,
                    label: c.corrente === 'AC' ? 'AC (lento)' : 'DC (rápido)',
                    value: c.kwh,
                    color: CORRENTE_COLORS[c.corrente],
                    detail: `${fmtInt(c.carregadores)} ${c.carregadores === 1 ? 'carregador' : 'carregadores'} · ${fmtInt(c.recargas)} recargas`
                  }))}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-x-3">
        <SectionTitle>
          {med.titulo} {por}
        </SectionTitle>
        <div className="mb-2 flex flex-wrap gap-1.5">
          <Toggle
            items={GRANULARIDADES}
            value={granEfetiva}
            onChange={setGran}
            disabled={(k) => k === 'hora' && !podeHora}
          />
          <Toggle items={MEDIDAS} value={medida} onChange={setMedida} />
        </div>
      </div>
      <div className={`rounded-3xl bg-white p-4 shadow-soft ${series.loading && s ? 'opacity-60' : ''}`}>
        {series.error && !s && <ErrorBox message={series.error} onRetry={series.reload} />}
        {s && (
          <StackedBars
            ariaLabel={`${med.titulo} ${por} por origem`}
            labels={s.labels}
            formatLabel={labelFormatter(s.granularidade)}
            formatValue={medida === 'kwh' ? fmtKwh : medida === 'receita' ? (v) => fmtBrl(v) : (v) => fmtInt(v)}
            previous={
              s.anterior
                ? {
                    label: s.anterior_parcial ? 'Período anterior (parcial)' : 'Período anterior',
                    values:
                      medida === 'kwh'
                        ? s.anterior.kwh
                        : medida === 'recargas'
                          ? s.anterior.recargas
                          : s.anterior.receita_brl.map(Number)
                  }
                : undefined
            }
            series={s.origens.map((og) => ({
              key: og.origem,
              label: og.label,
              color: originColor(og.origem),
              note: medida === 'receita' && og.estimada ? 'estimada' : undefined,
              values:
                medida === 'kwh'
                  ? s.kwh[og.origem] ?? []
                  : medida === 'recargas'
                    ? s.recargas[og.origem] ?? []
                    : (s.receita_brl[og.origem] ?? []).map(Number)
            }))}
          />
        )}
      </div>

      <SectionTitle>Quando os carros carregam</SectionTitle>
      <div className="rounded-3xl bg-white p-4 shadow-soft">
        {heat.data && <Heatmap dias={heat.data.dias} grid={heat.data.grid} formatKwh={fmtKwh} />}
        {heat.error && !heat.data && <ErrorBox message={heat.error} onRetry={heat.reload} />}
        <p className="m-0 mt-2 text-[11.5px] text-bow-royal/50">Hora de início da recarga, horário de Brasília.</p>
      </div>

      <SectionTitle>Por carregador</SectionTitle>
      {chargers.error && !chargers.data && <ErrorBox message={chargers.error} onRetry={chargers.reload} />}
      {chargers.data && (
        <div className="overflow-x-auto rounded-3xl bg-white shadow-soft">
          <table className="w-full min-w-[720px] border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.05em] text-bow-royal/55">
                <th className="px-4 py-3 font-semibold">Carregador</th>
                <th className="px-3 py-3 text-right font-semibold">Recargas</th>
                <th className="px-3 py-3 text-right font-semibold">Clientes</th>
                <th className="px-3 py-3 text-right font-semibold">kWh</th>
                <th className="px-3 py-3 text-right font-semibold">Receita</th>
                <th className="px-3 py-3 text-right font-semibold">Ocupação</th>
                <th className="px-3 py-3 text-right font-semibold">Disponib.</th>
                <th className="px-4 py-3 text-right font-semibold">Pot. média</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {chargers.data.carregadores.map((c) => (
                <tr key={c.hub_station_id} className="border-t border-bow-royal/[0.07]">
                  <td className="px-4 py-3">
                    <div className="font-semibold">{c.carregador}</div>
                    <div className="text-[11.5px] text-bow-royal/50">
                      {c.corrente} · {c.estacao ?? c.hub_station_id}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right">{fmtInt(c.recargas)}</td>
                  <td className="px-3 py-3 text-right">{fmtInt(c.clientes_unicos)}</td>
                  <td className="px-3 py-3 text-right">{fmtKwhNum(c.kwh)}</td>
                  <td className="px-3 py-3 text-right">{fmtBrl(c.receita_brl)}</td>
                  <td className="px-3 py-3 text-right">{fmtPct(c.ocupacao_pct)}</td>
                  <td className="px-3 py-3 text-right">{fmtPct(c.uptime_pct)}</td>
                  <td className="px-4 py-3 text-right">
                    {c.potencia_nominal_kw > 0
                      ? `${fmtKwhNum(c.potencia_efetiva_kw)} / ${fmtInt(c.potencia_nominal_kw)} kW`
                      : `${fmtKwhNum(c.potencia_efetiva_kw)} kW`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="m-0 mt-3 text-[11.5px] leading-snug text-bow-royal/50">
        Receita do app Bow = recargas concluídas e cobradas. Receita de roaming (Tupi, Spott, movE) é
        estimada pela tarifa contratada com cada operador — não é o preço que o app parceiro cobra do
        motorista. Clientes e NPS contam só o app Bow: os apps parceiros não identificam o motorista.
        Variação = comparação com o período anterior de mesmo tamanho; CO₂ evitado é estimado pela
        energia entregue (1,15 kg por kWh).
      </p>
    </div>
  );
}
